import { useCreateFlag, useUpdateFlag, useGetFlags } from "api/flags/service";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
} from "reactstrap";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import Select from "components/Select";
import SubHeader from "components/Navbars/SubHeader";

const FlagsAdd = ({
    activeColor = "secondary",
    shellThemeVariant = "1",
    isLightTheme = false,
}) => {
    const [isMobile, setIsMobile] = React.useState(
        () => typeof window !== "undefined" && window.innerWidth < 992
    );

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        isTheme2,
        inputStyle,
        inputClassName,
        selectStyles,
        theme2FieldsCss: baseTheme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        activeColorStyle,
        pageKey: "flags-add",
        insetClass: THEME2_ADMIN_INSETS.flags,
        withSelectStyles: true,
    });

    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? `${baseTheme2FieldsCss}
                  @media (max-width: 991.98px) {
                    .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.flags-add-page.flags-add-page--theme2.theme2-admin-content-compact.theme2-flags-inset {
                      padding-top: calc(74px + 45px) !important;
                    }
                  }
                  @media (max-width: 575.98px) {
                    .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.flags-add-page.flags-add-page--theme2.theme2-admin-content-compact.theme2-flags-inset {
                      padding-top: calc(64px + 45px) !important;
                    }
                  }`
                : "",
        [isTheme2, baseTheme2FieldsCss]
    );
    const { t } = useTranslation();
    const { showNotification } = useNotification();

    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const createFlagMutation = useCreateFlag();
    const updateFlagMutation = useUpdateFlag();
    const { data: flagsData } = useGetFlags(undefined, { enabled: isEditMode });

    const moduleOptions = [
        { value: 'inventory', label: t('flagsAdd.moduleOptions.inventory') },
        { value: 'users', label: t('flagsAdd.moduleOptions.users') },
        { value: 'deals', label: t('flagsAdd.moduleOptions.deals') },
        { value: 'reports', label: t('flagsAdd.moduleOptions.reports') },
        { value: 'accounts', label: t('flagsAdd.moduleOptions.accounts') },
    ];

    const priorityOptions = [
        { value: 'High', label: t('flagsAdd.priorityOptions.High') },
        { value: 'Medium', label: t('flagsAdd.priorityOptions.Medium') },
        { value: 'Low', label: t('flagsAdd.priorityOptions.Low') },
        { value: 'Informational', label: t('flagsAdd.priorityOptions.Informational') },
    ];

    const [formData, setFormData] = useState({
        documentId: '',
        name: '',
        description: '',
        module: 'inventory',
        priority: 'Low',
    });
    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        if (isEditMode && flagsData && flagsData.results) {
            const flag = flagsData.results.find(f => String(f.documentId) === String(id));
            if (flag) {
                setFormData({
                    documentId: flag.documentId,
                    name: flag.name || '',
                    description: flag.description || '',
                    module: flag.module || 'inventory',
                    priority: flag.priority || 'Low',
                });
            }
        }
    }, [isEditMode, flagsData, id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const missingFields = [];

        if (!formData.name.trim()) {
            newErrors.name = t('flagsAdd.flagNameRequired');
            missingFields.push(t('flagsAdd.flagName'));
        }

        setErrors(newErrors);

        // Show notification if there are errors
        if (Object.keys(newErrors).length > 0) {
            const fieldsList = missingFields.join(', ');
            showNotification(
                'danger',
                `${t('flagsAdd.requiredFieldsMissing')}: ${fieldsList}`
            );
            return false;
        }

        return true;
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        if (isEditMode) {
            updateFlagMutation.mutate({
                id: formData.documentId,
                data: {
                    name: formData.name,
                    description: formData.description,
                    module: formData.module,
                    priority: formData.priority,
                }
            });
        } else {
            createFlagMutation.mutate({
                name: formData.name,
                description: formData.description,
                module: formData.module,
                priority: formData.priority,
            });
        }
    };

    const handleCancel = () => {
        navigate('/flags');
    };

    return (
        <div
            className={contentClassName}
            style={{
                minHeight: "80vh",
                paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
            }}
        >
            <Theme2FormFieldsStyle css={theme2FieldsCss} />
            <div style={{ width: "100%" }}>
                <div className="flags-add-subheader-wrap">
                    <SubHeader
                        activeColor={activeColor}
                        back={-1}
                        backFallback="/flags"
                        title={isEditMode ? t("flagsAdd.titleEdit") : t("flagsAdd.titleAdd")}
                        theme2ScrollGapFill={isTheme2}
                        isLightTheme={isLightTheme}
                    />
                </div>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit} className={formFieldsClassName}>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="name" className="form-control-label">
                                                {t('flagsAdd.flagName')} <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.name)}
                                                style={inputStyle}
                                                placeholder={t('flagsAdd.enterFlagName')}
                                                disabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                            />
                                            {errors.name && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.name}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label for="module" className="form-control-label">
                                                {t('flagsAdd.module')}
                                            </Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                name="module"
                                                placeholder={t('flagsAdd.module')}
                                                value={moduleOptions.find(opt => opt.value === formData.module)}
                                                onChange={(option) => {
                                                    setFormData(prev => ({ ...prev, module: option?.value || '' }));
                                                    if (errors.module) {
                                                        setErrors(prev => ({ ...prev, module: '' }));
                                                    }
                                                }}
                                                options={moduleOptions}
                                                isDisabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label for="priority" className="form-control-label">
                                                {t('flagsAdd.priority')}
                                            </Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                name="priority"
                                                placeholder={t('flagsAdd.priority')}
                                                value={priorityOptions.find(opt => opt.value === formData.priority)}
                                                onChange={(option) => {
                                                    setFormData(prev => ({ ...prev, priority: option?.value || '' }));
                                                    if (errors.priority) {
                                                        setErrors(prev => ({ ...prev, priority: '' }));
                                                    }
                                                }}
                                                options={priorityOptions}
                                                isDisabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label for="description" className="form-control-label">
                                                {t('flagsAdd.description')}
                                            </Label>
                                            <Input
                                                id="description"
                                                name="description"
                                                type="textarea"
                                                rows="3"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.description)}
                                                style={inputStyle}
                                                placeholder={t('flagsAdd.enterDescription')}
                                                disabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                            />
                                            {errors.description && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.description}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* Bottom buttons removed for mobile-responsive consistency */}
                                <div className="d-none d-lg-block mt-4">
                                     {/* Keeping a small spacer for desktop if needed */}
                                </div>
                            </Form>
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-end">
                                <div className="d-none d-lg-flex align-items-center">
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        onClick={handleCancel}
                                        disabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                        style={{ marginRight: "10px" }}
                                    >
                                        {t('flagsAdd.cancel')}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: 'white' }}
                                        size="sm"
                                        onClick={handleSubmit}
                                        disabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                    >
                                        {createFlagMutation.isPending || updateFlagMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode ? t('flagsAdd.updating') : t('flagsAdd.creating')}
                                            </>
                                        ) : (
                                            <>
                                                <i className={isEditMode ? "tim-icons icon-pencil" : "tim-icons icon-simple-add"} style={{ marginRight: '8px' }} />
                                                {isEditMode ? t('flagsAdd.updateFlag') : t('flagsAdd.createFlag')}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                {/* Mobile View Buttons */}
                                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={handleCancel}
                                        disabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                        className="mb-2 w-100"
                                        style={{ padding: '12px' }}
                                    >
                                        {t('flagsAdd.cancel')}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: 'white', padding: '12px' }}
                                        onClick={handleSubmit}
                                        disabled={createFlagMutation.isPending || updateFlagMutation.isPending}
                                        className="w-100"
                                    >
                                        {createFlagMutation.isPending || updateFlagMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode ? t('flagsAdd.updating') : t('flagsAdd.creating')}
                                            </>
                                        ) : (
                                            <>
                                                <i className={isEditMode ? "tim-icons icon-pencil" : "tim-icons icon-simple-add"} style={{ marginRight: '8px' }} />
                                                {isEditMode ? t('flagsAdd.updateFlag') : t('flagsAdd.createFlag')}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
            </div>
        </div>
    );
};

export default FlagsAdd; 